import type { ProjectLaunchOrderData } from '@/components/Markdown/MarkdownViewer/CustomTagCom/ProjectLaunchOrder/const';

const PENDING_PAY_BEFORE_LOGIN_KEY = 'wegic:pending-pay-before-login';

type PendingPayBeforeLoginListener = () => void;

const pendingPayBeforeLoginListeners = new Set<PendingPayBeforeLoginListener>();
let volatilePendingPayBeforeLogin: PendingPayBeforeLoginContext | null = null;

export interface PendingPayBeforeLoginContext {
  projectId: string;
  order?: ProjectLaunchOrderData | null;
}

function getStorage(storage?: Storage): Storage | null {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isPendingPayBeforeLoginContext(value: unknown): value is PendingPayBeforeLoginContext {
  return typeof value === 'object'
    && value !== null
    && typeof (value as PendingPayBeforeLoginContext).projectId === 'string'
    && (value as PendingPayBeforeLoginContext).projectId.length > 0;
}

export function savePendingPayBeforeLogin(
  context: PendingPayBeforeLoginContext,
  storage?: Storage,
): boolean {
  const usesDefaultStorage = storage === undefined;
  const targetStorage = getStorage(storage);
  if (!targetStorage || !context.projectId) {
    if (usesDefaultStorage && context.projectId) {
      updateVolatilePendingPayBeforeLogin(context);
    }
    return false;
  }

  try {
    targetStorage.setItem(PENDING_PAY_BEFORE_LOGIN_KEY, JSON.stringify(context));
    if (usesDefaultStorage) {
      updateVolatilePendingPayBeforeLogin(null);
    }
    return true;
  } catch {
    if (usesDefaultStorage) {
      updateVolatilePendingPayBeforeLogin(context);
    }
    return false;
  }
}

export function getPendingPayBeforeLogin(storage?: Storage): PendingPayBeforeLoginContext | null {
  if (storage === undefined && volatilePendingPayBeforeLogin) {
    return volatilePendingPayBeforeLogin;
  }

  const targetStorage = getStorage(storage);
  if (!targetStorage) {
    return null;
  }

  try {
    const value = targetStorage.getItem(PENDING_PAY_BEFORE_LOGIN_KEY);
    if (!value) {
      return null;
    }

    const context: unknown = JSON.parse(value);
    return isPendingPayBeforeLoginContext(context) ? context : null;
  } catch {
    return null;
  }
}

export function clearPendingPayBeforeLogin(storage?: Storage): void {
  const usesDefaultStorage = storage === undefined;
  const targetStorage = getStorage(storage);
  if (targetStorage) {
    try {
      targetStorage.removeItem(PENDING_PAY_BEFORE_LOGIN_KEY);
    } catch {
      // Storage failures must not block login completion.
    }
  }

  if (usesDefaultStorage) {
    updateVolatilePendingPayBeforeLogin(null);
  }
}

function notifyPendingPayBeforeLoginListeners() {
  pendingPayBeforeLoginListeners.forEach(listener => listener());
}

function updateVolatilePendingPayBeforeLogin(context: PendingPayBeforeLoginContext | null) {
  volatilePendingPayBeforeLogin = context;
  notifyPendingPayBeforeLoginListeners();
}

export function subscribePendingPayBeforeLogin(listener: PendingPayBeforeLoginListener): () => void {
  pendingPayBeforeLoginListeners.add(listener);
  return () => pendingPayBeforeLoginListeners.delete(listener);
}

export function getPendingPayBeforeLoginProjectId(): string | null {
  return getPendingPayBeforeLogin()?.projectId ?? null;
}
